import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  Link,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TimerProvider } from './context/TimerContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import { DashboardProvider } from './context/DashboardContext';
import ToastContainer from './components/Toast';
import Login from './components/Login';
import TimeTracking from './pages/TimeTracking';
import EmployeeDashboard from './pages/EmployeeDashboard';
import MyTimeOff from './pages/MyTimeOff';
import MyCalendar from './pages/MyCalendar';
import MyExpenses from './pages/MyExpenses';
import ManagerDashboard from './components/manager/ManagerDashboard';
import OAuthCallback from './components/OAuthCallback';
import AcceptInvitation from './components/AcceptInvitation';
import { OperationalChatButton } from './components/operational';
import { AiAssistantPage } from './components/ai';
import { UserProfile } from './components/profile';
import { TimerWidget } from './components/timer';
import { NotificationBell } from './components/NotificationBell';
import ClausesPage from './components/locilex/ClausesPage';
import './App.css';

// Theme Toggle Component
const ThemeToggle: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      className="relative w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-105"
      style={{
        background: isDark ? 'rgba(51, 65, 85, 0.5)' : 'rgba(241, 245, 249, 1)',
        border: isDark
          ? '1px solid rgba(71, 85, 105, 0.5)'
          : '1px solid rgba(226, 232, 240, 1)',
      }}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {/* Sun icon */}
      <svg
        className={`w-5 h-5 absolute transition-all duration-300 ${
          isDark
            ? 'opacity-0 rotate-90 scale-0'
            : 'opacity-100 rotate-0 scale-100 text-amber-500'
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
      {/* Moon icon */}
      <svg
        className={`w-5 h-5 absolute transition-all duration-300 ${
          isDark
            ? 'opacity-100 rotate-0 scale-100 text-[#4F7DF3]'
            : 'opacity-0 -rotate-90 scale-0'
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
        />
      </svg>
    </button>
  );
};

function AppContent() {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [managementExpanded, setManagementExpanded] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  const hasEmployeeAccess =
    user?.role === 'developer' ||
    user?.role === 'analyst' ||
    user?.role === 'designer' ||
    user?.role === 'qa_engineer' ||
    user?.role === 'manager' ||
    user?.role === 'admin';
  const isContributorRole =
    user?.role === 'developer' ||
    user?.role === 'analyst' ||
    user?.role === 'designer' ||
    user?.role === 'qa_engineer';
  const userMenuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const managementNavigation = [
    {
      name: t('manager.overview'),
      href: '/manager',
      icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z',
    },
    {
      name: t('manager.projects'),
      href: '/manager/projects',
      icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
    },
    {
      name: t('manager.tasks'),
      href: '/manager/tasks',
      icon: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    },
    {
      name: t('manager.users'),
      href: '/manager/users',
      icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    },
    {
      name: t('manager.teams'),
      href: '/manager/teams',
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    },
    {
      name: t('manager.clients'),
      href: '/manager/clients',
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    },
    {
      name: t('manager.pricelists'),
      href: '/manager/pricelists',
      icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    {
      name: t('manager.timeEntries'),
      href: '/manager/time-entries',
      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    {
      name: t('manager.reports'),
      href: '/manager/reports',
      icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    },
    {
      name: t('manager.timeApproval'),
      href: '/manager/approvals',
      icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    {
      name: t('manager.timeOff'),
      href: '/manager/time-off',
      icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    },
    ...(isAdmin
      ? [
          {
            name: t('manager.aiTraining'),
            href: '/manager/ai-training',
            icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
          },
        ]
      : []),
  ];

  React.useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (location.pathname.startsWith('/manager')) {
      setManagementExpanded(true);
    }
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : 'bg-slate-50'}`}
      >
        <div className="flex flex-col items-center gap-6">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#4F7DF3] to-[#6366F1] flex items-center justify-center shadow-lg shadow-[#4F7DF3]/30 animate-pulse">
            <svg
              className="w-9 h-9 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const isActiveLink = (href: string) => {
    if (href === '/timesheet')
      return location.pathname.startsWith('/timesheet');
    return location.pathname === href;
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${isDark ? '' : 'bg-slate-50'}`}
    >
      {/* Minimal Top Bar - Theme Aware */}
      <nav
        className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-colors duration-300 ${
          isDark
            ? 'bg-[#0f172a]/95 border-slate-800/50'
            : 'bg-white/95 border-slate-200'
        }`}
      >
        <div className="px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4F7DF3] to-[#6366F1] flex items-center justify-center shadow-lg shadow-[#4F7DF3]/20 transition-all duration-300 group-hover:shadow-xl group-hover:shadow-[#4F7DF3]/30 group-hover:scale-105">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div className="hidden sm:block">
                <h1
                  className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                >
                  {t('app.title')}
                </h1>
                <p
                  className={`text-sm -mt-0.5 ${isDark ? 'text-slate-300' : 'text-slate-500'}`}
                >
                  {t('app.tagline')}
                </p>
              </div>
            </Link>

            {/* Main Navigation - Compact with theme support */}
            <div className="hidden md:flex items-center gap-1 ml-6">
              {(user?.role === 'manager' || user?.role === 'admin') && (
                <>
                  <Link
                    to="/employee-dashboard"
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActiveLink('/employee-dashboard')
                        ? 'bg-indigo-50 text-indigo-600'
                        : isDark
                          ? 'text-slate-200 hover:bg-slate-700/60 hover:text-white'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 10a3 3 0 11-6 0 3 3 0 016 0zm-9.879 7.804A13 13 0 0112 16c2.5 0 4.847.655 6.879 1.804M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="hidden lg:inline">
                      {t('dashboard.myDashboard')}
                    </span>
                  </Link>
                  <Link
                    to="/ai-assistant"
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActiveLink('/ai-assistant')
                        ? 'bg-indigo-50 text-indigo-600'
                        : isDark
                          ? 'text-slate-200 hover:bg-slate-700/60 hover:text-white'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    <span className="hidden lg:inline">
                      {t('nav.aiAssistant')}
                    </span>
                  </Link>
                  <Link
                    to="/manager"
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/manager')
                        ? 'bg-indigo-50 text-indigo-600'
                        : isDark
                          ? 'text-slate-200 hover:bg-slate-700/60 hover:text-white'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
                      />
                    </svg>
                    <span className="hidden lg:inline">
                      {t('nav.management')}
                    </span>
                  </Link>
                </>
              )}
              {isContributorRole && (
                <Link
                  to="/ai-assistant"
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActiveLink('/ai-assistant')
                      ? 'bg-[#4F7DF3]/20 text-[#4F7DF3]'
                      : isDark
                        ? 'text-slate-200 hover:bg-slate-700/60 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                  <span className="hidden lg:inline">
                    {t('nav.aiAssistant')}
                  </span>
                </Link>
              )}
              {/* Clauses lives in the sidebar nav (Work section) */}
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Timer Widget */}
              <TimerWidget />

              {/* User & Notifications Group */}
              <div className="flex items-center gap-1">
                {/* Notification Bell */}
                <NotificationBell />

                {/* User menu - hidden on mobile (user info in mobile menu) */}
                <div className="relative hidden sm:block" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className={`flex items-center gap-3 p-1.5 rounded-xl transition-all duration-200 group ${
                      isDark ? 'hover:bg-slate-900/50' : 'hover:bg-slate-100'
                    }`}
                  >
                    <div className="relative">
                      <div className="w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:shadow-indigo-500/30 transition-all duration-300">
                        <span className="text-sm font-bold text-white">
                          {user?.first_name?.[0]}
                          {user?.last_name?.[0]}
                        </span>
                      </div>
                      <div
                        className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 ${isDark ? 'border-[#0f172a]' : 'border-white'}`}
                      />
                    </div>

                    <svg
                      className={`w-4 h-4 hidden sm:block transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {userMenuOpen && (
                    <div
                      className={`absolute right-0 mt-2 w-72 rounded-2xl shadow-2xl py-2 z-50 animate-scale-in ${
                        isDark
                          ? 'bg-slate-900 border border-slate-700/50'
                          : 'bg-white border border-slate-200 shadow-xl'
                      }`}
                    >
                      <div
                        className={`px-5 py-4 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <span className="text-base font-bold text-white">
                              {user?.first_name?.[0]}
                              {user?.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <div
                              className={`text-base font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                            >
                              {user?.full_name}
                            </div>
                            <div
                              className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                            >
                              {user?.email}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="py-2">
                        <Link
                          to="/profile"
                          className={`flex items-center gap-3 px-5 py-3 text-base transition-colors ${
                            isDark
                              ? 'text-slate-300 hover:bg-slate-900/50 hover:text-white'
                              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                          }`}
                        >
                          <svg
                            className={`w-5 h-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          {t('profile.settings')}
                        </Link>
                        {isAdmin && (
                          <Link
                            to="/profile?tab=billing"
                            className={`flex items-center gap-3 px-5 py-3 text-base transition-colors ${
                              isDark
                                ? 'text-slate-300 hover:bg-slate-900/50 hover:text-white'
                                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                            }`}
                          >
                            <svg
                              className={`w-5 h-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                              />
                            </svg>
                            {t('profile.billing')}
                          </Link>
                        )}
                      </div>

                      <div
                        className={`border-t py-2 ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
                      >
                        <div
                          className={`flex items-center gap-2 mx-5 mb-2 px-3 py-1.5 rounded-lg text-sm ${
                            isDark
                              ? 'bg-amber-500/10 text-amber-400'
                              : 'bg-amber-50 text-amber-600'
                          }`}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M12 2L9.5 7H6l1.5 3.5L4 15h4l1.5 3.5L12 22l2.5-3.5L16 15h4l-3.5-4.5L18 7h-3.5L12 2z" />
                          </svg>
                          <span>Fait au Québec</span>
                        </div>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            logout();
                          }}
                          className={`flex items-center gap-3 w-full px-5 py-3 text-base transition-colors ${
                            isDark
                              ? 'text-rose-400 hover:bg-rose-500/10'
                              : 'text-rose-600 hover:bg-rose-50'
                          }`}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          {t('app.logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden p-2 rounded-xl transition-all ${
                  isDark
                    ? 'text-slate-300 hover:bg-slate-900/50 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                {mobileMenuOpen ? (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile menu - Theme Aware */}
      <div
        className={`md:hidden fixed top-0 right-0 bottom-0 w-80 z-50 shadow-2xl transform transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col ${
          isDark
            ? 'bg-slate-900 border-l border-slate-800/50'
            : 'bg-white border-l border-slate-200'
        }`}
      >
        <div
          className={`flex-shrink-0 flex items-center justify-between p-5 border-b ${isDark ? 'border-slate-800/50' : 'border-slate-200'}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <span
              className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('app.title')}
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className={`p-2 rounded-xl transition-colors ${isDark ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-500 hover:text-slate-900'}`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* User */}
        <div
          className={`flex-shrink-0 p-5 border-b ${isDark ? 'border-slate-800/50' : 'border-slate-200'}`}
        >
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <span className="text-lg font-bold text-white">
                {user?.first_name?.[0]}
                {user?.last_name?.[0]}
              </span>
            </div>
            <div>
              <div
                className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {user?.full_name}
              </div>
              {user?.role && (
                <div className="inline-flex items-center px-2 py-0.5 bg-indigo-500/10 text-indigo-500 rounded-md text-sm font-medium">
                  {t(`roles.${user.role}`)}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <Link
              to="/profile"
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${isDark ? 'bg-slate-800 text-white hover:bg-slate-700 hover:text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'}`}
            >
              {t('profile.settings')}
            </Link>
            {isAdmin && (
              <Link
                to="/profile?tab=billing"
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${isDark ? 'bg-slate-800 text-white hover:bg-slate-700 hover:text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'}`}
              >
                {t('profile.billing')}
              </Link>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 min-h-0 p-5 overflow-y-auto">
          <ul className="space-y-2">
            {hasEmployeeAccess && (
              <li>
                <Link
                  to="/employee-dashboard"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all ${location.pathname === '/employee-dashboard' ? 'bg-indigo-500/10 text-indigo-500' : isDark ? 'text-white hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M3 12l2-3m0 0l7-4 7 4M5 9v10a1 1 0 001 1h12a1 1 0 001-1V9m-9 11l4-4m0 0l4 4m-4-4v4"
                    />
                  </svg>
                  Dashboard
                </Link>
              </li>
            )}
            {isManagerOrAdmin && (
              <>
                <li>
                  <Link
                    to="/timesheet"
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all ${location.pathname.startsWith('/timesheet') ? 'bg-indigo-500/10 text-indigo-500' : isDark ? 'text-white hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {t('nav.timesheet')}
                  </Link>
                </li>
                <li>
                  <button
                    onClick={() => setManagementExpanded(!managementExpanded)}
                    className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium transition-all ${location.pathname.startsWith('/manager') ? 'bg-indigo-500/10 text-indigo-500' : isDark ? 'text-white hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
                      />
                    </svg>
                    <span className="flex-1 text-left">
                      {t('nav.management')}
                    </span>
                    <svg
                      className={`w-5 h-5 transition-transform ${managementExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${managementExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <ul
                      className={`mt-2 ml-5 space-y-1 border-l-2 pl-4 ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
                    >
                      {managementNavigation.map((item) => (
                        <li key={item.href}>
                          <Link
                            to={item.href}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                              location.pathname === item.href ||
                              (item.href !== '/manager' &&
                                location.pathname.startsWith(item.href))
                                ? 'bg-indigo-500/10 text-indigo-500 font-medium'
                                : isDark
                                  ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
                                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                            }`}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d={item.icon}
                              />
                            </svg>
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              </>
            )}
            <li>
              <Link
                to="/ai-assistant"
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all ${location.pathname === '/ai-assistant' ? 'bg-indigo-500/10 text-indigo-500' : isDark ? 'text-white hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                {t('nav.aiAssistant')}
              </Link>
            </li>
            <li>
              <Link
                to="/clauses"
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all ${location.pathname === '/clauses' ? 'bg-indigo-500/10 text-indigo-500' : isDark ? 'text-white hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {t('nav.clauses')}
              </Link>
            </li>
          </ul>
        </nav>

        {/* Footer */}
        <div
          className={`flex-shrink-0 p-5 border-t ${isDark ? 'border-slate-800/50 bg-slate-900/50' : 'border-slate-200 bg-slate-50'}`}
        >
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm mb-4 ${isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'}`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L9.5 7H6l1.5 3.5L4 15h4l1.5 3.5L12 22l2.5-3.5L16 15h4l-3.5-4.5L18 7h-3.5L12 2z" />
            </svg>
            <span>Fait au Québec</span>
          </div>
          <button
            onClick={() => {
              setMobileMenuOpen(false);
              logout();
            }}
            className={`flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl font-medium transition-colors ${isDark ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20' : 'bg-rose-50 text-rose-600 hover:bg-rose-100'}`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            {t('app.logout')}
          </button>
        </div>
      </div>

      {/* Main */}
      <Routes>
        <Route
          path="/"
          element={
            isManagerOrAdmin ? (
              <Navigate to="/manager" replace />
            ) : (
              <Navigate to="/employee-dashboard" replace />
            )
          }
        />
        <Route path="/timesheet/*" element={<TimeTracking />} />
        <Route path="/ai-assistant" element={<AiAssistantPage />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route
          path="/employee-dashboard"
          element={
            hasEmployeeAccess ? (
              <EmployeeDashboard />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/my-time-off"
          element={
            hasEmployeeAccess ? (
              <MyTimeOff />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/calendar"
          element={
            hasEmployeeAccess ? (
              <MyCalendar />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/expenses"
          element={
            hasEmployeeAccess ? (
              <MyExpenses />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/clauses" element={<ClausesPage />} />
        <Route path="/billing" element={<Navigate to="/profile" replace />} />
        <Route
          path="/manager/*"
          element={
            isManagerOrAdmin ? (
              <ManagerDashboard />
            ) : (
              <Navigate to="/timesheet" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.pathname === '/oauth/callback') return <OAuthCallback />;
  if (location.pathname.startsWith('/invitations/'))
    return (
      <Routes>
        <Route path="/invitations/:token" element={<AcceptInvitation />} />
      </Routes>
    );
  return <AppContent />;
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <ToastProvider>
          <ConfirmProvider>
            <AuthProvider>
              <TimerProvider>
                <DashboardProvider>
                  <AppRoutes />
                  <ToastContainer />
                  <OperationalChatButton />
                </DashboardProvider>
              </TimerProvider>
            </AuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
